import React, { useMemo, useState } from "react";
import { processSmartDelivery } from "../../api/smartDeliveryService";

import {
    PageHeader,
    Panel,
    InfoRow,
    KpiCard,
} from "../../components/ui/AdminUI";

import {
    NumberInput,
    PrimaryButton,
    SecondaryButton,
} from "../../components/ui/FormControls";

const DEFAULT_MATRIX = "0,10,15\n10,0,20\n15,20,0";

const SmartDeliveryPage = () => {
    const [parcelId, setParcelId] = useState("");
    const [matrixText, setMatrixText] = useState(DEFAULT_MATRIX);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const parsedMatrix = useMemo(() => {
        try {
            return parseMatrix(matrixText);
        } catch {
            return [];
        }
    }, [matrixText]);

    const matrixSize = parsedMatrix.length;

    const handleProcess = async () => {
        setError("");
        setResult(null);

        const numericParcelId = Number(parcelId);

        if (!Number.isInteger(numericParcelId) || numericParcelId <= 0) {
            setError("Parcel ID must be a positive integer.");
            return;
        }

        let distances;

        try {
            distances = parseMatrix(matrixText);
            validateMatrix(distances);
        } catch (err) {
            setError(err.message || "Invalid distance matrix.");
            return;
        }

        try {
            setLoading(true);

            const data = await processSmartDelivery(
                numericParcelId,
                distances
            );

            setResult(data);
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.response?.data?.title ||
                err?.message ||
                "Smart delivery processing failed."
            );
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setParcelId("");
        setMatrixText(DEFAULT_MATRIX);
        setResult(null);
        setError("");
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Smart Delivery Management"
                subtitle="Optimize parcel routes and calculate delivery cost using the intelligent logistics engine."
                action={
                    <SecondaryButton onClick={resetForm}>
                        Reset
                    </SecondaryButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <KpiCard
                    title="Parcel"
                    value={parcelId || "—"}
                    color="blue"
                />

                <KpiCard
                    title="Matrix Size"
                    value={matrixSize ? `${matrixSize} × ${matrixSize}` : "—"}
                    color="purple"
                />

                <KpiCard
                    title="Engine Status"
                    value={loading ? "Processing" : result ? "Completed" : "Ready"}
                    color={loading ? "yellow" : result ? "green" : "slate"}
                />
            </div>

            <Panel
                title="Delivery Input"
                subtitle="Enter parcel ID and a square comma-separated distance matrix."
            >
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div>
                        <NumberInput
                            label="Parcel ID"
                            value={parcelId}
                            onChange={(e) => setParcelId(e.target.value)}
                            placeholder="Enter parcel ID"
                            min="1"
                        />
                    </div>

                    <div className="xl:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            Distance Matrix
                        </label>

                        <textarea
                            value={matrixText}
                            onChange={(e) => setMatrixText(e.target.value)}
                            rows={8}
                            spellCheck={false}
                            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 font-mono text-sm shadow-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-200"
                            placeholder={"0,10,15\n10,0,20\n15,20,0"}
                        />

                        <p className="mt-2 text-sm text-slate-500">
                            Each row must contain the same number of numeric values.
                            Example: a 3 × 3 matrix contains three rows with three values each.
                        </p>
                    </div>
                </div>

                <div className="mt-5">
                    <PrimaryButton
                        onClick={handleProcess}
                        disabled={loading}
                    >
                        {loading
                            ? "Processing..."
                            : "Process Smart Delivery"}
                    </PrimaryButton>
                </div>
            </Panel>

            {result && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                        <KpiCard
                            title="Total Distance"
                            value={formatNumber(result.totalDistance, "km")}
                            color="blue"
                        />

                        <KpiCard
                            title="Parcel Weight"
                            value={formatNumber(result.weightKg, "kg")}
                            color="purple"
                        />

                        <KpiCard
                            title="Cargo Value"
                            value={formatMoney(result.cargoValue)}
                            color="teal"
                        />

                        <KpiCard
                            title="Total Cost"
                            value={formatMoney(result.totalCost)}
                            color="green"
                        />
                    </div>

                    <Panel
                        title="Optimized Delivery Result"
                        subtitle="Route and calculated delivery cost returned by the Smart Delivery engine."
                    >
                        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                            <InfoRow
                                label="Tracking Number"
                                value={result.trackingNumber || "—"}
                            />

                            <InfoRow
                                label="Parcel ID"
                                value={result.parcelId ?? parcelId}
                            />

                            <InfoRow
                                label="Total Distance"
                                value={formatNumber(result.totalDistance, "km")}
                            />

                            <InfoRow
                                label="Weight"
                                value={formatNumber(result.weightKg, "kg")}
                            />
                        </div>

                        <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5">
                            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
                                Optimized Route
                            </p>

                            <p className="mt-2 break-words text-xl font-black text-blue-900">
                                {formatRoute(result.optimizedRoute)}
                            </p>
                        </div>
                    </Panel>

                    <Panel
                        title="Cost Breakdown"
                        subtitle="Calculated logistics cost components."
                    >
                        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                            <InfoRow
                                label="Package Cost"
                                value={formatMoney(result.packageCost)}
                            />

                            <InfoRow
                                label="Distance Cost"
                                value={formatMoney(result.distanceCost)}
                            />

                            <InfoRow
                                label="Weight Cost"
                                value={formatMoney(result.weightCost)}
                            />

                            <InfoRow
                                label="Cargo Value"
                                value={formatMoney(result.cargoValue)}
                            />
                        </div>

                        <div className="mt-6 flex flex-col gap-2 rounded-2xl border border-green-200 bg-green-50 p-6 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-wide text-green-700">
                                    Final Delivery Cost
                                </p>
                                <p className="mt-1 text-slate-600">
                                    Total calculated by the Smart Delivery service.
                                </p>
                            </div>

                            <div className="text-4xl font-black text-green-800">
                                {formatMoney(result.totalCost)}
                            </div>
                        </div>
                    </Panel>
                </>
            )}
        </div>
    );
};

function parseMatrix(text) {
    const trimmed = String(text || "").trim();

    if (!trimmed) {
        throw new Error("Distance matrix is required.");
    }

    return trimmed.split(/\r?\n/).map((row, rowIndex) => {
        const values = row
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value !== "");

        if (values.length === 0) {
            throw new Error(`Matrix row ${rowIndex + 1} is empty.`);
        }

        return values.map((value, columnIndex) => {
            const number = Number(value);

            if (!Number.isFinite(number)) {
                throw new Error(
                    `Invalid number in row ${rowIndex + 1}, column ${columnIndex + 1
                    }.`
                );
            }

            if (number < 0) {
                throw new Error(
                    `Distance cannot be negative in row ${rowIndex + 1
                    }, column ${columnIndex + 1}.`
                );
            }

            return number;
        });
    });
}

function validateMatrix(matrix) {
    if (!Array.isArray(matrix) || matrix.length < 2) {
        throw new Error("Distance matrix must contain at least two rows.");
    }

    const size = matrix.length;

    matrix.forEach((row, index) => {
        if (!Array.isArray(row) || row.length !== size) {
            throw new Error(
                `Distance matrix must be square. Row ${index + 1
                } contains ${row?.length ?? 0} values instead of ${size}.`
            );
        }
    });
}

function formatRoute(route) {
    if (!Array.isArray(route) || route.length === 0) {
        return "—";
    }

    return route.join(" → ");
}

function formatMoney(value) {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return "—";
    }

    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "EUR",
    }).format(number);
}

function formatNumber(value, suffix = "") {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return "—";
    }

    return `${number.toFixed(2)}${suffix ? ` ${suffix}` : ""}`;
}

export default SmartDeliveryPage;